import type {CourseApiModel} from '../../../api/course';
import type {MembershipApiModel} from '../../../api/membership';
import type {Expand, MaybeExpanded} from '../../../api/types';
import type {CoreCoachCoursesListParams} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {currentUserStore} from '../../../api/user';
import {apiRequest} from '../../../api/utils/apiRequest';
import {isIamCoach} from '../../../utils/entity/isIamCoach';
import {envManager} from '../../../utils/env';
import {expandResponse} from '../utils';

export {courseFetchList};

type Config = BaseConfig<CoreCoachCoursesListParams> & {
  asCoach?: {
    fetchMyClientCourses?: boolean;
  };
};

const strategy: AsyncStrategy = async (roles, config) => {
  return isIamCoach() ? roles.coach(config) : roles.client(config);
};

async function courseFetchList(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

courseFetchList.asClient = asClient;
courseFetchList.asCoach = asCoach;

async function asClient(config: Config): Promise<CourseApiModel[]> {
  const expand: Expand = {
    membership: [['course_id', null, config.params.expand ?? {}]],
  };

  const query = config.params.query || [];

  if (Array.isArray(query)) {
    query.push([
      'status',
      '!in',
      ['proposed', 'requested', envManager.isCore ? 'declined' : null].filter(
        Boolean,
      ) as MembershipApiModel['status'][],
    ]);
  }

  const patchedConfig = {
    ...config,
    params: {
      ...config.params,
      query,
    },
  };

  const response = await apiRequest<CoreCoachCoursesListParams>({
    method: 'client.memberships.list',
    params: {...patchedConfig.params, expand, limit: 999},
  });

  const memberships = expandResponse(response, {
    ...patchedConfig,
    params: {...patchedConfig.params, expand},
  }) as (MembershipApiModel & {course: MaybeExpanded<CourseApiModel>})[];

  return memberships.map(membership => membership.course).filter(Boolean);
}

async function asCoach(config: Config): Promise<CourseApiModel[]> {
  let myClientCourses: CourseApiModel[] = [];

  if (
    config.asCoach?.fetchMyClientCourses === undefined ||
    config.asCoach.fetchMyClientCourses
  ) {
    myClientCourses = await asClient(config);
  }

  const query = config.params.query || [];

  if (Array.isArray(query)) {
    query.push([
      'hidden_for_ids',
      '!contains',
      currentUserStore.user?._id ? [currentUserStore.user._id] : [],
    ]);
  }

  const patchedConfig = {
    ...config,
    params: {
      ...config.params,
      query,
    },
  };

  const response = await apiRequest<CoreCoachCoursesListParams>({
    method: 'coach.courses.list',
    ...patchedConfig,
  });

  const courses = expandResponse(response, patchedConfig);

  return [...courses, ...myClientCourses];
}
